import React, { createContext, useContext, useState, useEffect } from 'react';
import { getPortalStatus } from '../Services/services';
import { applyBrandingDocument } from '../utils/brandingDocument';

// Maps the small set of admin-selectable font presets to a real CSS font stack.
// Keeping this as a fixed lookup (rather than accepting arbitrary CSS) avoids
// injecting untrusted values into stylesheets.
export const FONT_FAMILY_PRESETS = {
  default: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  arial: "Arial, Helvetica, sans-serif",
  georgia: "Georgia, 'Times New Roman', serif",
  'times-new-roman': "'Times New Roman', Times, serif",
  verdana: "Verdana, Geneva, sans-serif",
  'courier-new': "'Courier New', Courier, monospace",
};

// Default branding values
const DEFAULT_BRANDING = {
  companyName: '',
  primaryColor: '#0066cc',
  darkColor: '#003366',
  lightColor: '#ffffff',
  logoUrl: null,
  fontFamily: 'default',
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  isLoading: true,
});

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await getPortalStatus();
        const data = response.data;
        
        if (data?.branding) {
          setBranding({
            companyName: data.branding.companyName ?? '',
            primaryColor: data.branding.primaryColor || DEFAULT_BRANDING.primaryColor,
            darkColor: data.branding.darkColor || DEFAULT_BRANDING.darkColor,
            lightColor: data.branding.lightColor || DEFAULT_BRANDING.lightColor,
            logoUrl: data.branding.logoUrl || null,
            fontFamily: data.branding.fontFamily || DEFAULT_BRANDING.fontFamily,
          });
        }
      } catch (error) {
        console.log('Failed to fetch branding, using defaults:', error);
        // Keep default branding on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranding();
  }, []);

  // Inject CSS custom properties for easy global access
  useEffect(() => {
    if (!isLoading) {
      document.documentElement.style.setProperty('--brand-primary-color', branding.primaryColor);
      document.documentElement.style.setProperty('--brand-dark-color', branding.darkColor);
      document.documentElement.style.setProperty('--brand-light-color', branding.lightColor);
      const fontStack = FONT_FAMILY_PRESETS[branding.fontFamily] || FONT_FAMILY_PRESETS.default;
      document.documentElement.style.setProperty('--brand-font-family', fontStack);
      document.body.style.fontFamily = fontStack;
    }
  }, [branding, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      applyBrandingDocument({
        companyName: branding.companyName,
        logoUrl: branding.logoUrl,
      });
    }
  }, [branding.companyName, branding.logoUrl, isLoading]);

  return (
    <BrandingContext.Provider value={{ branding, isLoading }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

export { DEFAULT_BRANDING };
export default BrandingContext;
